import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  ArrowsLeftRightIcon,
  BarStackChart,
  Button,
  formatData,
  getGridColorsForCount,
  Layout,
  Legend,
  Tabs,
  tooltipFormatter,
  truncateTextWithEllipsis,
  useLocalStorage,
} from '@utilities';

import { DISPLAY_AS } from '../../utilities/helpers';
import GridTable from '../GridTable';
import NoDataPlaceholder from '../NoDataPlaceholder';

import styles from './_index.module.scss';

const Grid = ({
  compareByGroups,
  data,
  displayAs = DISPLAY_AS.PERCENT.value,
  isAskWhySurvey = false,
  onTop2BoxApplied,
  question,
  selectedCompareBy,
  surveyId,
}) => {
  const [areColorsReversed, setAreColorsReversed] = useLocalStorage(
    `survey-${surveyId}-${question.id}-colors-reversed`,
    false
  );
  const [selectedCompareByTab, setSelectedCompareByTab] = useState();

  const isDisplayAsPercent = displayAs === DISPLAY_AS.PERCENT.value;
  const isMonadicSectionQuestion = Boolean(question?.concepts?.length);
  const isOverallCompareBy = selectedCompareBy?.value === 'overall';
  // sequential-monadic grid responses arrive as per-compare-by-group slices
  const isCompareBySegmented = !isOverallCompareBy && Boolean(data?.dataByCompareBy);
  const chartKeyToDisplay = isDisplayAsPercent ? 'chartPct' : 'chartCount';
  const compareByTabs = useMemo(() => {
    if (!isCompareBySegmented) return [];

    return (
      compareByGroups
        ?.filter(({ id, name }) => id != null || name != null)
        .map(({ id, name }) => ({
          label: name ?? id,
          value: String(id ?? name),
        })) || []
    );
  }, [compareByGroups, isCompareBySegmented]);

  const activeCompareByTab =
    compareByTabs.find(({ value }) => value === selectedCompareByTab)?.value ||
    compareByTabs[0]?.value;

  // resolve the active tab (keyed by group id) back to the group name the backend nests data under
  const activeGroupName = compareByGroups?.find(
    ({ id, name }) => String(id ?? name) === activeCompareByTab
  )?.name;
  const displayData = isCompareBySegmented ? data.dataByCompareBy[activeGroupName] : data;
  const tableToDisplay = isDisplayAsPercent ? displayData?.tablePct : displayData?.tableCount;

  const colors = useMemo(() => {
    if (!displayData) return [];
    const colors = getGridColorsForCount(displayData.segments?.[0]?.chartCount?.series?.length);
    return areColorsReversed ? colors.toReversed() : colors;
  }, [areColorsReversed, displayData]);

  useEffect(() => {
    setSelectedCompareByTab((currentTab) =>
      compareByTabs.some(({ value }) => value === currentTab) ? currentTab : compareByTabs[0]?.value
    );
  }, [compareByTabs]);

  if (!displayData) {
    return (
      <Layout.Flex.Column className={styles['grid']} gap="x-large">
        {compareByTabs.length > 0 && (
          <Tabs
            aria-label="Compare by group"
            onChange={setSelectedCompareByTab}
            options={compareByTabs}
            value={activeCompareByTab}
          />
        )}
        <NoDataPlaceholder />
      </Layout.Flex.Column>
    );
  }

  return (
    <Layout.Flex.Column className={styles['grid']} gap="x-large">
      {compareByTabs.length > 0 && (
        <Tabs
          aria-label="Compare by group"
          onChange={setSelectedCompareByTab}
          options={compareByTabs}
          value={activeCompareByTab}
        />
      )}
      <Layout.Flex.Column gap="small">
        <div className={styles['grid-chart-controls']}>
          <h4 className={styles['grid-label']}>{`Answer Options by ${
            isDisplayAsPercent ? DISPLAY_AS.PERCENT.label : DISPLAY_AS.COUNT.label
          }`}</h4>
          <div>
            <Button
              icon={
                <ArrowsLeftRightIcon
                  className={classNames(styles['grid-color-invert-icon'], {
                    [styles['is-reversed']]: areColorsReversed,
                  })}
                />
              }
              onClick={() => setAreColorsReversed((prev) => !prev)}
              text="Invert Colors"
              variant="secondary"
            />
          </div>
        </div>

        {displayData?.segments?.map((segment, index) => {
          const chartToDisplay = segment[chartKeyToDisplay];
          const isLastSegment = index === displayData.segments.length - 1;

          const barHeight = 16;
          const heightForLegend = 32;
          const minHeightForLabel = 20;
          const minHeightForTooltip = 50;
          const paddingBetweenBars = (minHeightForLabel - barHeight) / 2;
          const chartHeight =
            chartToDisplay.xAxis.categories.length * (minHeightForLabel + paddingBetweenBars);
          return (
            <BarStackChart
              chart={{
                height: isLastSegment
                  ? Math.max(chartHeight + heightForLegend, minHeightForTooltip + heightForLegend)
                  : Math.max(chartHeight, minHeightForTooltip),
                marginRight: 15,
                spacing: [0, 0, 0, 0],
              }}
              chartTitle={segment.name}
              className={styles['grid-chart']}
              colors={colors}
              hasLegend={false}
              key={segment.id}
              plotOptions={{
                series: {
                  groupPadding: 0,
                  pointPadding: 0,
                  pointWidth: barHeight,
                },
              }}
              series={chartToDisplay.series}
              tooltip={{
                formatter: function () {
                  return tooltipFormatter({
                    data: this,
                    format: segment[chartKeyToDisplay]?.format,
                  });
                },
              }}
              xAxis={{
                ...segment[chartKeyToDisplay]?.xAxis,
                labels: {
                  align: 'left',
                  formatter: function () {
                    const isOverall = selectedCompareBy?.value === 'overall';
                    const color = isOverall
                      ? compareByGroups?.[0]?.color
                      : compareByGroups?.find(({ id, name }) => [id, name].includes(this.value))
                          ?.color;
                    const escapeHtml = (value) =>
                      String(value)
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/"/g, '&quot;')
                        .replace(/'/g, '&#39;');

                    // monadic-section grids show the payload category (like every other response);
                    // a normal grid keeps the single "Overall" label
                    const rawText =
                      isOverall && !isMonadicSectionQuestion
                        ? compareByGroups?.[0]?.name
                        : truncateTextWithEllipsis({ length: 25, text: this.value });
                    const text = escapeHtml(rawText);
                    // monadic-section grid labels are concepts, not compare-by groups, so drop the dot
                    const dot = isMonadicSectionQuestion
                      ? ''
                      : `<span class="${styles['grid-xaxis-dot']}" style="background:${color}"></span>`;
                    return `<div class="${styles['grid-xaxis-label']}">${dot}${text}</div>`;
                  },
                  reserveSpace: true,
                  useHTML: true,
                },
              }}
              yAxis={{
                labels: {
                  formatter: function () {
                    return isLastSegment
                      ? formatData({
                          format: chartToDisplay.format,
                          value: this.value,
                        })
                      : '';
                  },
                },
                max: isDisplayAsPercent ? 1 : undefined,
                tickAmount: isDisplayAsPercent ? 11 : undefined,
                visible: true,
              }}
            />
          );
        })}
        <Legend
          className={styles['grid-legend']}
          isHorizontal
          items={displayData?.segments?.[0]?.chartCount?.series.map(({ name }, index) => ({
            color: colors[index],
            name,
          }))}
        />
      </Layout.Flex.Column>
      <hr />
      <GridTable
        columns={tableToDisplay?.columns}
        compareByGroups={compareByGroups}
        data={tableToDisplay?.data}
        displayAs={displayAs}
        isAskWhySurvey={isAskWhySurvey}
        onTop2BoxApplied={onTop2BoxApplied}
        question={question}
        surveyId={surveyId}
      />
    </Layout.Flex.Column>
  );
};

Grid.propTypes = {
  compareByGroups: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.object,
  displayAs: PropTypes.string,
  isAskWhySurvey: PropTypes.bool,
  onTop2BoxApplied: PropTypes.func,
  question: PropTypes.object,
  selectedCompareBy: PropTypes.object,
  surveyId: PropTypes.number,
};

export default Grid;
